// The captcha is conditional and programmatic: nothing is visible until a solve actually
// runs. The server decides when one is owed and says so with `X-Captcha: required`; this is
// the half that hears that, solves a proof-of-work, and retries once.

import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "./client";
import { usePublicSettings } from "./queries";
import { nativeRuntime, type NativeRuntime } from "../platform/native";

/** How long to wait for the frame. Cap's own instrumentation timeout is 20s and a solve is
 *  well under a second, so anything past this is a frame that will never answer. */
const SOLVE_TIMEOUT_MS = 30_000;

/** Solve one challenge with no visible widget, in the runner document.
 *
 *  The widget does not run here. It runs in /captcha-frame.html, which carries its own
 *  Content-Security-Policy because Cap's instrumentation payload needs `unsafe-eval` and
 *  this document must not have it -- see CAPTCHA_FRAME_CSP in api/web.py. That also keeps
 *  the widget's 44 kB out of the main bundle entirely: the frame is only ever loaded by a
 *  login that has already failed once. */
export async function solveInFrame(
  endpoint: string,
  runtime: Pick<NativeRuntime, "solveCaptcha"> | null = nativeRuntime(),
  signal?: AbortSignal,
): Promise<string> {
  if (runtime) {
    return signal ? runtime.solveCaptcha(endpoint, signal) : runtime.solveCaptcha(endpoint);
  }
  if (signal?.aborted) throw new DOMException("The operation was aborted", "AbortError");
  const frame = document.createElement("iframe");
  frame.src = `/captcha-frame.html?ep=${encodeURIComponent(endpoint)}`;
  // Never seen and never focusable: this is a worker that happens to be a document.
  frame.setAttribute("aria-hidden", "true");
  frame.setAttribute("tabindex", "-1");
  frame.style.cssText = "position:absolute;width:0;height:0;border:0";

  const { promise, resolve, reject } = Promise.withResolvers<string>();

  const timer = window.setTimeout(() => {
    stop();
    reject(new Error("cap: the captcha frame never answered"));
  }, SOLVE_TIMEOUT_MS);

  const abort = () => {
    stop();
    reject(new DOMException("The operation was aborted", "AbortError"));
  };

  function stop(): void {
    window.clearTimeout(timer);
    window.removeEventListener("message", onMessage);
    signal?.removeEventListener("abort", abort);
    frame.remove();
  }

  function onMessage(event: MessageEvent): void {
    // Both checks matter: the origin because any document may post here, and the source
    // because every other frame on this origin shares that origin.
    if (event.origin !== window.location.origin) return;
    if (event.source !== frame.contentWindow) return;
    const data: unknown = event.data;
    if (typeof data !== "object" || data === null) return;
    const result = data as { kind?: unknown; token?: unknown; error?: unknown };
    if (result.kind !== "cap:result") return;
    stop();
    if (typeof result.token === "string" && result.token) {
      resolve(result.token);
      return;
    }
    reject(
      new Error(typeof result.error === "string" ? result.error : "cap: challenge not solved"),
    );
  }

  window.addEventListener("message", onMessage);
  signal?.addEventListener("abort", abort, { once: true });
  document.body.appendChild(frame);
  return promise;
}

/** Runs a submit, and if the server answers "captcha required", solves one and runs it
 *  again -- once. `verifying` is true only while a solve is in flight, which is the only
 *  moment the user is told anything about a captcha. */
export function useCaptcha(runtime = nativeRuntime()) {
  const endpoint = usePublicSettings().data?.captcha_endpoint ?? null;
  const [verifying, setVerifying] = useState(false);
  const [failed, setFailed] = useState(false);
  const active = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      active.current?.abort();
      active.current = null;
    },
    [],
  );

  const guard = useCallback(
    async (submit: (token?: string) => Promise<unknown>) => {
      active.current?.abort();
      const controller = new AbortController();
      active.current = controller;
      setFailed(false);
      try {
        try {
          await submit();
          return;
        } catch (error) {
          // Errors are swallowed on purpose: both callers drive a react-query mutation, which
          // has already recorded the failure on `mutation.error` for the dialog to render. A
          // rethrow here would only become an unhandled rejection.
          if (!(error instanceof ApiError) || error.captcha !== "required" || !endpoint) return;
        }
        if (controller.signal.aborted) return;
        setVerifying(true);
        let token: string;
        try {
          token = await solveInFrame(endpoint, runtime, controller.signal);
        } catch {
          if (!controller.signal.aborted) setFailed(true);
          return;
        } finally {
          if (active.current === controller) setVerifying(false);
        }
        if (controller.signal.aborted || active.current !== controller) return;
        try {
          await submit(token);
        } catch (error) {
          if (error instanceof ApiError && error.captcha === "failed") setFailed(true);
        }
      } finally {
        if (active.current === controller) active.current = null;
      }
    },
    [endpoint, runtime],
  );

  return { guard, verifying, failed };
}
